/**
 * RIJ custom JS — runs on every page.
 *
 * 1. Sticky ATC bar rating (Judge.me + JSON-LD fallback).
 * 2. Color-group product switching: SPA swap via Shopify Section Rendering
 *    API (dfyne-style), instant image-first feedback, session-cached
 *    sections, swatch click + mobile swipe triggers.
 */
(function () {
  const NS = window.RIJ = window.RIJ || {};

  // Prefetch every color-group sibling's hero (360px) IMMEDIATELY on page
  // load — the first swatch click / swipe after a refresh must be a cache
  // hit (swatch thumbs already load eagerly; heroes are small and load in
  // parallel without blocking first paint). Uses the same dedupe set as the
  // swipe preloader below. Also warms the sticky-bar image, which the theme
  // lazy-loads.
  (function prefetchColorGroupHeroes() {
    const preloaded = NS._preloadedImages || (NS._preloadedImages = new Set());
    const preload = (src) => {
      if (!src || preloaded.has(src)) return;
      preloaded.add(src);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    };
    const cgs = document.querySelector('.custom-color-group-switcher');
    if (cgs) {
      [].slice.call(cgs.querySelectorAll('a')).forEach((a) => {
        const img = a.querySelector('img');
        if (!img || !img.src) return;
        // 360 = the one URL the strip, bar and gallery share (swatch block
        // renders it, optimistic swaps use it). 120 = what the theme's
        // section morph renders for the bar afterwards — prefetch both so
        // the switch never waits on the network, before or after the morph.
        preload(img.src.replace(/width=\d+/, 'width=120'));
        preload(img.src.replace(/width=\d+/, 'width=360'));
      });
    }
  })();

  // crypto.randomUUID is undefined in insecure contexts (http://LAN-IP) and
  // old Safari. Shopify's web-pixels / theme code call it during SPA swaps
  // (product:viewed etc.) and crash, which can blank the page mid-switch.
  // Polyfill it so the swap never trips over the missing API.
  if (typeof crypto !== 'undefined' && crypto && !crypto.randomUUID) {
    try {
      crypto.randomUUID = function randomUUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = Math.random() * 16 | 0;
          return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
        });
      };
    } catch (e) { /* some engines disallow assignment — ignore */ }
  }


  // SPA swap between color-group products: fetch only the page's sections
  // via Shopify's Section Rendering API (a few KB) instead of the whole
  // page (~340KB) — the same mechanism dfyne uses. URL updates via
  // pushState immediately; content lands when the fetch resolves. The page
  // never reloads, white-flashes or jumps to the top.
  //
  // Session-scoped sections cache: within one visit, re-clicking a color
  // applies its sections HTML straight from memory (zero network, zero
  // staleness risk — a reload clears it), so "keep clicking" feels instant.
  const sectionCache = new Map();
  const CACHE_MAX = 12;
  const sectionQuery = (() => {
    const main = document.querySelector('#MainContent, main');
    const ids = main
      ? [].slice.call(main.querySelectorAll(':scope > .shopify-section'))
          .map((s) => s.id.replace(/^shopify-section-/, ''))
          .filter(Boolean)
          .join(',')
      : '';
    return ids ? 'sections=' + ids : '';
  })();

  // Warm the section cache on page load: every color-group sibling's
  // section HTML is fetched once in the background so the FIRST click after
  // a refresh also applies instantly (variant picker, price, stock). The
  // cache is memory-only — a reload clears it, so nothing can go stale
  // beyond the visit.
  (function prefetchColorGroupSections() {
    const cgs = document.querySelector('.custom-color-group-switcher');
    if (!cgs || !sectionQuery) return;
    [].slice.call(cgs.querySelectorAll('a')).forEach((a) => {
      const href = a.getAttribute('href');
      if (!href || href === location.pathname) return;
      const secUrl = href + (href.includes('?') ? '&' : '?') + sectionQuery;
      if (sectionCache.has(secUrl)) return;
      fetch(secUrl)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error('sections ' + r.status))))
        .then((data) => {
          if (sectionCache.size >= CACHE_MAX) sectionCache.delete(sectionCache.keys().next().value);
          sectionCache.set(secUrl, data);
        })
        .catch(() => { /* prefetch is best-effort — a click falls back to fetch */ });
    });
  })();

  NS.switchProduct = function switchProduct(url) {
    if (!url || url === location.pathname) return;

    // Latest-intent guard: rapid clicks race their fetches. Old requests are
    // left to finish in the background so their responses still land in the
    // session cache (re-clicking any color then applies instantly), but only
    // the most recent click may apply content or fall back. Stale
    // responses/errors are dropped — the page never hard-navigates to an
    // old URL (white flash) or shows mismatched content.
    NS._latestUrl = url;
    history.pushState({ rijSpa: true }, '', url);

    const secUrl = url + (url.includes('?') ? '&' : '?') + sectionQuery;
    const isLatest = () => NS._latestUrl === url;

    const apply = (data) => {
      if (!isLatest()) return;
      let replaced = 0;
      for (const key in data) {
        const live = document.getElementById('shopify-section-' + key);
        if (!live) continue;
        try {
          const doc = new DOMParser().parseFromString(data[key], 'text/html');
          const fresh = doc.body.firstElementChild;
          if (!fresh) continue;
          // replaceWith re-executes <script>s (external ones re-fetch and
          // run too). Everything here is either data or a one-time component
          // definition from the initial page load — strip scripts so the
          // swap has zero re-execution side effects (duplicate defines,
          // re-init navigations). IIFE-wrapped inline scripts are kept only
          // when they wire per-instance UI (e.g. the popup drawer).
          [].slice.call(fresh.querySelectorAll('script[src]')).forEach((s) => s.remove());
          [].slice.call(fresh.querySelectorAll('script:not([src])')).forEach((s) => {
            const t = s.textContent || '';
            if (/customElements\.define/.test(t)) { s.remove(); return; }
            if (/^(const|let|var)\s+\w/.test(t.trim().split('\n')[0])) s.remove();
          });
          live.replaceWith(fresh);
          replaced++;
        } catch (err) { /* one bad section must not break the swap */ }
      }
      if (replaced > 0) NS._afterSpa(url, {});
    };

    const cached = sectionCache.get(secUrl);
    if (cached) {
      apply(cached);
      return;
    }

    // Fetch with one retry. On persistent failure we stay put (URL already
    // updated, content self-heals on the next click) — never hard-navigate,
    // which would white-flash the page mid-switch.
    const load = (attempt) => {
      fetch(secUrl)
        .then((r) => r.ok ? r.json() : Promise.reject(new Error('sections ' + r.status)))
        .then((data) => {
          // Cache every completed render — even superseded ones are worth
          // keeping for the next click.
          if (sectionCache.size >= CACHE_MAX) sectionCache.delete(sectionCache.keys().next().value);
          sectionCache.set(secUrl, data);
          if (!isLatest()) return;
          apply(data);
        })
        .catch((err) => {
          if (!isLatest()) return; // superseded — do nothing
          if (attempt < 1) { load(attempt + 1); return; }
          // Theme's combined-listings morph as a gentler fallback.
          const vp = document.querySelector('variant-picker');
          if (vp && typeof vp.fetchUpdatedSection === 'function') {
            NS._spaPending = url;
            vp.fetchUpdatedSection(url, { morphElementSelector: 'main', detail: {} });
          }
        });
    };
    load(0);
  };

  // Swap the currently visible gallery slide's image to `src` — instant
  // visual feedback (dfyne's "image first" trick). Optimistic: the src is
  // set immediately without waiting for the image to load, so the swipe
  // always responds (the preload usually makes it a cache hit; otherwise
  // the slide's placeholder background covers the brief load).
  NS._swapVisibleHero = function swapVisibleHero(src) {
    if (!src) return;
    const ss = document.querySelector('slideshow-slides');
    if (!ss) return;
    let best = null, bestDist = Infinity;
    [].slice.call(ss.querySelectorAll('slideshow-slide')).forEach(s => {
      const d = Math.abs(s.offsetLeft - ss.scrollLeft);
      if (d < bestDist) { bestDist = d; best = s; }
    });
    const img = best && best.querySelector('img');
    if (!img) return;
    // A lingering srcset overrides src (source selection prefers srcset),
    // so the optimistic swap would paint nothing until the progressive
    // upgrade cleared it. Clear it here — the swap must be visible NOW.
    img.srcset = '';
    img.src = src;
  };

  // Instantly move the active swatch highlight (the black border) when a
  // color is picked — the section HTML catches up later and stays in sync.
  NS._setActiveSwatch = function setActiveSwatch(url) {
    [].slice.call(document.querySelectorAll('.custom-color-group-switcher a')).forEach(a => {
      const on = a.getAttribute('href') === url;
      a.classList.toggle('is-active', on);
      if (on) a.setAttribute('aria-current', 'page');
      else a.removeAttribute('aria-current');
    });
  };

  // Clicking a color-group swatch switches products in place (SPA), on any
  // viewport — same as the mobile swipe but pointer-driven. Registered in
  // the CAPTURE phase so no other script can stop propagation and let the
  // swatch's default navigation (full page load → white flash) slip through.
  // preventDefault runs for EVERY swatch click — including the already-active
  // one — because `_setActiveSwatch` marks colors active instantly, so a
  // rapid re-click on a just-picked color would otherwise fall through to
  // the anchor's default navigation and hard-reload the page.
  if (!NS._cgsClickWired) {
    NS._cgsClickWired = true;
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const swatch = t.closest('.custom-color-group-switcher a');
      if (!swatch) return;
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      const url = swatch.getAttribute('href');
      if (swatch.classList.contains('is-active')) return; // current color: no-op
      // Instant visual feedback, same as dfyne: swap the visible slide to
      // the clicked color's hero (idle-preloaded) and highlight the swatch
      // before the content lands.
      NS._setActiveSwatch(url);
      const thumb = swatch.querySelector('img');
      if (thumb) NS._swapVisibleHero(thumb.src.replace(/width=\d+/, 'width=360'));
      NS.switchProduct(url);
    }, true);
  }

  /**
   * Swipe between products of the color group.
   *
   * On mobile product pages the previous/next product's FIRST image is
   * bookended onto the gallery as a plain scroll-snap slide (a "virtual
   * slide" — a div, NOT a slideshow-slide / [ref] element, so the theme's
   * dots, counter and select() indexes never see it). Because it is a real
   * slide in the native scroll-snap gallery, swiping past the last image
   * pulls the next product in exactly like swiping to the next image of the
   * same product: 1:1 tracking, momentum, snap — the browser does it all,
   * zero JS during the gesture. When the scroller settles on a virtual
   * slide we commit the SPA switch. Reuses the same SPA swap as swatch
   * clicks — no page reload.
   */
  NS.initCombinedSwipe = function initCombinedSwipe() {
    // data-template lives on <main> (theme.liquid), not <body>.
    const main = document.querySelector('#MainContent, main');
    if (!main || main.dataset.template !== 'product') return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    const cgs = document.querySelector('.custom-color-group-switcher');
    const slideshow = document.querySelector('slideshow-slides');
    if (!cgs || !slideshow) return;

    // The SPA morph may patch the slideshow node in place (keeping our old
    // listeners with a stale prev/next closure) — always tear down the
    // previous binding before re-binding.
    if (typeof slideshow._rijSwipeTeardown === 'function') slideshow._rijSwipeTeardown();

    const swatches = [].slice.call(cgs.querySelectorAll('a'));
    if (swatches.length < 2) return;

    const currentIdx = swatches.findIndex(a => a.classList.contains('is-active'));
    if (currentIdx < 0) return;

    const prevUrl = currentIdx > 0 ? swatches[currentIdx - 1].getAttribute('href') : null;
    const nextUrl = currentIdx < swatches.length - 1 ? swatches[currentIdx + 1].getAttribute('href') : null;
    if (!prevUrl && !nextUrl) return;

    let fired = false;

    // Dedupe sets live at module scope so SPA re-binds don't keep appending
    // <link> nodes to <head>.
    const preloadedImages = NS._preloadedImages || (NS._preloadedImages = new Set());
    const HERO_WIDTH = 360;
    const preloadImage = (src) => {
      if (!src || preloadedImages.has(src)) return;
      preloadedImages.add(src);
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
    };
    const thumbOf = (idx) => {
      const img = swatches[idx] && swatches[idx].querySelector('img');
      return img ? img.src : null;
    };
    const heroOf = (idx) => {
      const t = thumbOf(idx);
      return t ? t.replace(/width=\d+/, 'width=' + HERO_WIDTH) : null;
    };
    // Adjacent heroes immediately (not idle): the first swipe in either
    // direction must already be a cache hit for instant image-first feedback.
    if (nextUrl) preloadImage(heroOf(currentIdx + 1));
    if (prevUrl) preloadImage(heroOf(currentIdx - 1));

    // Hover / touch on a swatch warms that color's thumb + hero from the CDN.
    swatches.forEach(a => {
      const warm = () => {
        const img = a.querySelector('img');
        if (img) {
          preloadImage(img.src);
          preloadImage(img.src.replace(/width=\d+/, 'width=' + HERO_WIDTH));
        }
      };
      a.addEventListener('mouseenter', warm, { passive: true });
      a.addEventListener('touchstart', warm, { passive: true });
    });

    // --- Virtual slides ---------------------------------------------------
    // Bookend the gallery with the neighbor product's first image so edge
    // swipes are the SAME native scroll-snap as in-product image swipes:
    // 1:1 tracking, momentum, snap — the browser handles the gesture, this
    // code only decides when to switch. The virtual slide is a plain div
    // (no slideshow-slide tag, no ref) so the theme's dots/counter/select()
    // indexes are never affected.
    const virtualSlide = (url, heroSrc, title) => {
      if (!heroSrc) return null;
      const real = slideshow.querySelector('slideshow-slide');
      const div = document.createElement('div');
      div.className = 'rij-virtual-slide';
      div.dataset.rijVirtual = url;
      if (real) div.style.height = real.getBoundingClientRect().height + 'px';
      const img = document.createElement('img');
      img.src = heroSrc;
      img.alt = title || '';
      div.appendChild(img);
      return div;
    };

    let switched = false;
    let touching = false;
    let settleTimer = null;
    // Separate from settleTimer: onScroll clears settleTimer on every frame, so
    // sharing it killed the self-heal timeout and left `switched` stuck true
    // forever (one swipe switched product, every later swipe was ignored).
    let healTimer = null;

    const commit = (url) => {
      if (switched || !url) return;
      switched = true;
      NS._setActiveSwatch(url);
      NS.switchProduct(url);
      // Self-heal a failed switch (no SPA apply): release the guard so the
      // user can retry with another swipe. If the apply DID land, the
      // gallery was replaced and this stale binding is gone anyway.
      clearTimeout(healTimer);
      healTimer = setTimeout(() => {
        switched = false;
        // Switch did not land: do not strand the shopper on a neighbour image
        // (the theme's dots/counter cannot track a virtual slide) — snap back.
        if (location.pathname !== url) {
          const firstRealEl = slideshow.querySelector('slideshow-slide');
          if (firstRealEl && typeof slideshow.scrollTo === 'function') {
            slideshow.scrollTo({ left: firstRealEl.offsetLeft, behavior: 'smooth' });
          }
        }
      }, 2500);
    };

    // scroll-snap only lands on fully-visible slides, so "the scroller has
    // been idle AND the viewport-center slide is a virtual one" is a
    // deliberate commit gesture — the user swiped into the neighbor product.
    // Center-offset cache (measured once when the virtual slides are added).
    // Reading offsetLeft/offsetWidth inside a scroll handler forces a LAYOUT
    // for every slide on every scroll frame — the main-thread killer behind
    // TBT / Style & Layout. scrollLeft + clientWidth are cheap reads (they
    // don't invalidate layout), so the handlers just combine them with the
    // cached centers measured once.
    let slideCenters = null;
    const measureCenters = () => {
      slideCenters = [];
      [].slice.call(slideshow.querySelectorAll('slideshow-slide, .rij-virtual-slide')).forEach((s) => {
        slideCenters.push({ c: s.offsetLeft + s.offsetWidth / 2, url: s.dataset.rijVirtual || null });
      });
    };
    const nearest = () => {
      if (!slideCenters) return null;
      const viewCenter = slideshow.scrollLeft + slideshow.clientWidth / 2;
      let best = null, bestDist = Infinity;
      slideCenters.forEach((s) => {
        const d = Math.abs(s.c - viewCenter);
        if (d < bestDist) { bestDist = d; best = s; }
      });
      return best;
    };
    const checkSettled = () => {
      if (switched || touching) return;
      const best = nearest();
      if (best && best.url) commit(best.url);
    };
    const onScroll = () => {
      // Commit as soon as the snap animation pulls a virtual slide to the
      // viewport center. Position comes from the cached centers measured
      // once — reading offsetLeft/offsetWidth here would reflow every frame.
      if (!touching && !switched) {
        const viewCenter = slideshow.scrollLeft + slideshow.clientWidth / 2;
        let best = null, bestDist = Infinity;
        (slideCenters || []).forEach((s) => {
          if (!s.url) return;               // only virtual slides switch products
          const d = Math.abs(s.c - viewCenter);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        if (best && bestDist < 30) commit(best.url);
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(checkSettled, 180);
    };
    const onTouchStart = () => {
      touching = true;
      clearTimeout(settleTimer);
    };
    const onTouchEnd = () => {
      touching = false;
      // Let scroll-snap resolve the release, then re-check once it settles.
      clearTimeout(settleTimer);
      settleTimer = setTimeout(checkSettled, 120);
    };

    // Append the next-product slide; prepend the previous one. Prepending
    // shifts the real slides right by one viewport — jump back so the view
    // stays on the first real slide (instant, so no flash).
    let firstReal = null;
    if (prevUrl) {
      const v = virtualSlide(prevUrl, heroOf(currentIdx - 1), swatches[currentIdx - 1].title);
      if (v) { slideshow.insertBefore(v, slideshow.firstElementChild); firstReal = v.nextElementSibling; }
    }
    if (nextUrl) {
      const v = virtualSlide(nextUrl, heroOf(currentIdx + 1), swatches[currentIdx + 1].title);
      if (v) slideshow.appendChild(v);
    }
    if (firstReal) slideshow.scrollTo({ left: firstReal.offsetLeft, behavior: 'instant' });

    slideshow.addEventListener('scroll', onScroll, { passive: true });
    slideshow.addEventListener('touchstart', onTouchStart, { passive: true });
    slideshow.addEventListener('touchend', onTouchEnd, { passive: true });

    slideshow._rijSwipeTeardown = () => {
      clearTimeout(settleTimer);
      clearTimeout(healTimer);
      slideshow.removeEventListener('scroll', onScroll);
      slideshow.removeEventListener('touchstart', onTouchStart);
      slideshow.removeEventListener('touchend', onTouchEnd);
      [].slice.call(slideshow.querySelectorAll('.rij-virtual-slide')).forEach((v) => v.remove());
      delete slideshow._rijSwipeTeardown;
    };
  };

  // Post-SPA-swap wiring: variant-picker.fetchUpdatedSection morphs <main>
  // and broadcasts shopify:product:select. When its promise resolves the new
  // DOM is in place — refresh the bits the morph doesn't handle itself.
  if (!NS._spaWired) {
    NS._spaWired = true;

    // morph() merges the media gallery positionally and can leave stale
    // slides behind (the theme's own replaceWith becomes a no-op once morph
    // already replaced the node). Swap in a clean gallery. Preferred source:
    // the morph's own fetched page — unless morph moved its slides into the
    // live DOM already (which hollows it), then fall back to a fresh fetch.
    const refreshGallery = (url, detailHtml) => {
      const doSwap = (fresh, live) => {
        if (fresh && live && fresh !== live) live.replaceWith(fresh);
      };
      const live = document.querySelector('media-gallery');
      const fromDetail = detailHtml && detailHtml.querySelector('media-gallery');
      if (fromDetail && fromDetail.querySelector('slideshow-slide')) {
        doSwap(fromDetail, live);
        return;
      }
      fetch(url)
        .then((r) => r.ok ? r.text() : Promise.reject(new Error(String(r.status))))
        .then((txt) => {
          const doc = new DOMParser().parseFromString(txt, 'text/html');
          doSwap(doc.querySelector('media-gallery'), document.querySelector('media-gallery'));
        })
        .catch(() => { /* keep whatever is showing */ });
    };

    // Progressive image upgrade (restored 2026-09-04): the gallery renders
    // small (360w for a fast mobile first paint), then the larger version loads
    // in the background and swaps in for crispness. Only images in/near the
    // viewport get upgraded, and the big URL is stable so the HTTP cache serves
    // it on the next visit/switch. Scroll-triggered via IntersectionObserver.
    NS._upgradeGallery = function upgradeGallery(root) {
      const HI_WIDTH = 1200;
      const slides = [].slice.call((root || document).querySelectorAll('slideshow-slide'));
      const tryUpgrade = (slide) => {
        const img = slide && slide.querySelector('img.product-media__image');
        if (!img || img.dataset.rijHi) return;
        img.dataset.rijHi = '1';
        const base = img.src;
        const hi = new Image();
        hi.onload = () => {
          // The swipe's optimistic hero swap may have changed src while the
          // big image was in flight — don't clobber it with the old product.
          if (img.src !== base) return;
          img.src = hi.src;
          // The srcset still lists only small candidates — clear it so a
          // reflow/resize can't flip the image back to the low-res one.
          img.srcset = '';
          img.dataset.rijHi = 'done';
        };
        hi.src = img.src.replace(/width=\d+/, 'width=' + HI_WIDTH);
      };
      slides.forEach((slide) => {
        const r = slide.getBoundingClientRect();
        if (r.width > 0 && r.right > 0 && r.left < window.innerWidth) tryUpgrade(slide);
        else {
          // Note: `.observe()` returns undefined — chaining it into the
          // assignment would store undefined and the IO callback's
          // `_rijIo.disconnect()` would crash on a missing observer.
          slide._rijIo = slide._rijIo || new IntersectionObserver((entries) => {
            entries.forEach((en) => {
              if (en.isIntersecting) { tryUpgrade(en.target); en.target._rijIo.disconnect(); }
            });
          }, { rootMargin: '300px' });
          slide._rijIo.observe(slide);
        }
      });
    };
    NS._afterSpa = function afterSpa(url, opts) {
      if (opts && opts.title) {
        document.title = opts.title;
      } else {
        const activeSwatch = document.querySelector('.custom-color-group-switcher a.is-active');
        if (activeSwatch) {
          const suffix = document.title.includes('–') ? document.title.split('–').pop() : '';
          document.title = (activeSwatch.title || '').trim() + (suffix ? ' – ' + suffix.trim() : '');
        }
      }

      if (opts && opts.galleryHtml) refreshGallery(url, opts.galleryHtml);

      // Judge.me badges don't re-render into replaced/morphed DOM by themselves.
      if (window.jdgm && typeof window.jdgm.customizeBadges === 'function') {
        try { window.jdgm.customizeBadges(); } catch (err) { /* noop */ }
      }

      // Judge.me v3 review widget is a Vue app mounted once on page load:
      // review_widget.js runs a WidgetInitializer that scans
      // `.jdgm-review-widget[data-entry-point]` and mounts each container.
      // The SPA swap brings in a brand-new container node the original
      // initializer never sees, so the review section stays blank.
      // Two steps to revive it:
      // 1) The widget block ships an inline `script.jdgm-review-widget-data`
      //    that fills `jdgm.data.reviewWidget[productId]` (rating summary,
      //    sort config). Morphed scripts never execute — without this the
      //    manager crashes on `.sort_key`. Clone it into a real script node
      //    so the browser runs it (replaceWith keeps it idempotent).
      // 2) Re-import the entry module with a cache-busting query to spin up
      //    a NEW WidgetInitializer (imports resolve to cached modules) which
      //    scans the DOM and mounts our fresh container.
      const reviewWidget = document.querySelector('.jdgm-review-widget[data-entry-point]');
      if (reviewWidget && !reviewWidget.__vue_app__) {
        // First paint's widget block ships with a `jdgm-horizon-widget` class
        // on its `.shopify-app-block` (Judge.me's own CSS gives that combo
        // `width:100% !important` so it stretches inside the flex parent).
        // The morph swaps in a fresh block WITHOUT that class, collapsing the
        // container to 0 width and wrapping the stars vertically. Re-add the
        // class so Judge.me's own layout rule applies.
        const appBlock = reviewWidget.closest('.shopify-app-block');
        if (appBlock) appBlock.classList.add('jdgm-horizon-widget');

        document.querySelectorAll('script.jdgm-review-widget-data').forEach((inert) => {
          const live = document.createElement('script');
          live.className = inert.className;
          live.textContent = inert.textContent;
          inert.replaceWith(live);
        });
        const entryScript = document.querySelector('script[src*="review_widget.js"]');
        if (entryScript) {
          const sep = entryScript.src.indexOf('?') >= 0 ? '&' : '?';
          import(entryScript.src + sep + 'rijcb=' + Date.now()).catch(() => { /* leave blank */ });
        }
      }

      // The recently-viewed AI block is a custom element whose class survives
      // a morph — force a re-upgrade so it records and shows the new product.
      const rvList = document.querySelector('[data-products-container]');
      const rv = rvList && rvList.closest('[data-max-products]');
      if (rv) rv.replaceWith(rv.cloneNode(true));

      // Open the new product on its first image, then re-bind our swipe
      // logic against the fresh DOM.
      const gallery = document.querySelector('slideshow-slides');
      if (gallery) gallery.scrollLeft = 0;
      NS.initCombinedSwipe();
    };

    document.addEventListener('shopify:product:select', (e) => {
      if (!NS._spaPending || !e.promise) return;
      const url = NS._spaPending;
      NS._spaPending = null;
      e.promise.then(({ detail }) => {
        const title = detail && detail.html && detail.html.querySelector('title');
        NS._afterSpa(url, { title: title ? title.textContent : null, galleryHtml: detail && detail.html });
      }).catch(() => {
        // Morph fallback failed — stay put (no hard navigation / white
        // flash); the URL already updated and the next click re-attempts.
      });
    });

    // Back/forward after an SPA swap restores the URL but not the swapped
    // DOM. No reload here on purpose: the CGS swatches re-sync on the next
    // click, and a back/forward gesture must never white-flash the page.
  }

  // -------------------------------------------------------------------------
  // Optimistic buy-button state (instant sold-out / add-to-cart feedback).
  // The theme only updates the Add-to-cart button after the variant-change
  // section fetch resolves (~2s), so picking a sold-out size leaves the
  // button looking addable the whole time. The variant-picker embeds the full
  // variant list (each with `available`) in a JSON script — read it on every
  // option change and flip the button immediately. The theme's post-fetch
  // morph reconciles to the same state, so this only paints the end state
  // earlier (the theme's add-queue already handles clicks mid-switch).
  if (!NS._optiBuyWired) {
    NS._optiBuyWired = true;

    // Storefront button labels — must match locales/en.default.json
    // (products.product.add_to_cart / sold_out). Single source for every
    // optimistic relabel this module does: main buy button + sticky bar.
    const ADD_TEXT = 'ADD TO BAG';
    const SOLD_OUT_TEXT = 'SEE RELATED ITEMS';
    const isProductBuyButton = (b) => !b.closest('product-card, quick-add-component');

    // Remember the last-known "Add to cart" button innerHTML (icon + text) so
    // we can restore it when flipping back from a sold-out state.
    const snapshotAddState = () => {
      document.querySelectorAll('button[name="add"]').forEach((b) => {
        if (isProductBuyButton(b) && !b.disabled && /add/i.test(b.textContent || '')) {
          NS._addBtnHtml = b.innerHTML;
        }
      });
    };

    // Availability from the checked option values: an option value marked
    // unavailable (`data-option-available="false"`) means no variant
    // containing it has stock, so the current selection is DEFINITELY sold
    // out. All-available is only a hint (a rare checkerboard availability can
    // still make the exact combo sold out) — the theme's fetch corrects that
    // within a couple of seconds either way.
    const getSelectedAvailability = () => {
      const vp = document.querySelector('variant-picker');
      if (!vp) return null;
      const radios = vp.querySelectorAll('input[type="radio"]:checked');
      if (!radios.length) return null;
      let anyUnavailable = false;
      radios.forEach((r) => {
        if (r.dataset.optionAvailable === 'false') anyUnavailable = true;
      });
      return !anyUnavailable;
    };

    const applyBuyState = (available) => {
      // Mirror the sold-out state on <html> so CSS can key off a class instead
      // of a root-level :has() (which the browser re-validates on every DOM
      // mutation — expensive while the gallery morphs during a swipe).
      document.documentElement.classList.toggle('rij-sold-out', !available);
      document.querySelectorAll('button[name="add"]').forEach((btn) => {
        if (!isProductBuyButton(btn)) return;
        if (available) {
          if (btn.textContent.trim() !== ADD_TEXT) {
            btn.innerHTML = NS._addBtnHtml || ADD_TEXT;
          }
          btn.disabled = false;
        } else {
          if (btn.textContent.trim() !== SOLD_OUT_TEXT) {
            if (!NS._addBtnHtml) NS._addBtnHtml = btn.innerHTML;
            btn.innerHTML = SOLD_OUT_TEXT;
          }
          // Sold out: always keep it clickable — the server renders the button
          // disabled and the morph can re-add it, so unset it unconditionally.
          // The capture handler below turns the click into "scroll to the
          // recommendations" instead of an (impossible) add-to-cart submit.
          btn.disabled = false;
        }
      });

      // Payment (accelerated-checkout) buttons: the theme only updates these
      // after the slow variant-change section fetch resolves, so a sold-out
      // selection can leave PayPal / "More payment options" visible for seconds
      // (or forever if that fetch is aborted). Toggle the same `hidden`
      // attribute the theme uses, instantly, from the selected option
      // availability. The theme's post-fetch morph reconciles to the same
      // state, so setting the same attribute here cannot conflict with it.
      document.querySelectorAll('[ref="acceleratedCheckoutButtonContainer"]').forEach((acc) => {
        if (available) {
          acc.removeAttribute('hidden');
        } else {
          acc.setAttribute('hidden', 'true');
        }
      });

      // Sticky bar: reflect the sold-out state instantly too (the theme only
      // morphs it after the slow section fetch). Setting the same
      // data-variant-available attribute the CSS keys off, plus the label,
      // converges with the theme's post-fetch render. The button stays
      // clickable so SEE RELATED ITEMS can scroll; the fallback skips adding.
      const sbBar = document.querySelector('sticky-add-to-cart');
      if (sbBar) {
        sbBar.setAttribute('data-variant-available', String(available));
        const sbBtn = sbBar.querySelector('[ref="addToCartButton"]');
        if (sbBtn) {
          // never disable — sold-out must stay clickable to scroll to related.
          sbBtn.removeAttribute('disabled');
          // Official DOM label lives in .add-to-cart-text__content > span.
          const textEl = sbBtn.querySelector('.add-to-cart-text__content > span:first-of-type');
          if (textEl) textEl.textContent = available ? ADD_TEXT : SOLD_OUT_TEXT;
        }
      }
    };

    // Expose to NS so the outer-scope initial _selectListenersRun (which lives
    // OUTSIDE this if-block and can't see these consts) can call them — this
    // was throwing `getSelectedAvailability is not defined` (Uncaught).
    NS._buyState = { getSelectedAvailability, applyBuyState };

    // Capture-phase so it runs before the theme's own fetch kicks off (the
    // radio is already checked by the time `change` dispatches).
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (!t || typeof t.matches !== 'function') return;
      if (!t.matches('variant-picker input[type="radio"]')) return;
      snapshotAddState();
      const available = getSelectedAvailability();
      if (available !== null) applyBuyState(available);
    }, true);
  }

  // Initial paint: set the sticky bar label to ADD TO BAG / SEE RELATED ITEMS.
  // The <sticky-add-to-cart> web component upgrades asynchronously, so wait for
  // it to define + upgrade (otherwise [ref="addToCartButton"] doesn't exist and
  // the official component's own initial render sets "Add to cart").
  NS._selectListenersRun = () => {
    const bs = NS._buyState;
    if (!bs) return;
    const avail = bs.getSelectedAvailability();
    if (avail !== null) bs.applyBuyState(avail);
  };
  if ('whenDefined' in customElements) {
    customElements.whenDefined('sticky-add-to-cart').then(() => {
      setTimeout(NS._selectListenersRun, 0);
    }).catch(() => {});
  } else {
    NS._selectListenersRun();
  }

  NS.initCombinedSwipe();

  // ------------------------------------------------------------------------
  // Sticky bar add-to-cart fallback
  // The editor has the main buy button (buy-buttons > add-to-cart sub-block)
  // disabled, so the theme's sticky-bar handler no-ops: it puppets the main
  // button, which no longer renders. Submit the product form through the
  // theme's own AJAX on:submit flow instead. Document-level capture keeps it
  // wired across variant morphs, and the `[ref="addToCartButton"]` presence
  // check hands control back the moment the main button is re-enabled.
  // ------------------------------------------------------------------------
  if (!NS._stickySubmitWired) {
    NS._stickySubmitWired = true;
    document.addEventListener(
      'click',
      (e) => {
        const barButton = e.target.closest?.('sticky-add-to-cart .sticky-add-to-cart__button');
        if (!barButton) return;
        // Sold-out shows "SEE RELATED ITEMS" — scroll to related products even
        // though the official component renders the button disabled (we drive
        // the scroll ourselves, so don't bail on disabled for the sold-out case).
        const bar = barButton.closest('sticky-add-to-cart');
        if (bar?.dataset.variantAvailable === 'false') {
          const related =
            document.querySelector('product-recommendations, .product-recommendations, [data-testid="product-recommendations-section"]');
          if (related) related.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (barButton.disabled) return;
        const productForm = document.querySelector('product-form-component');
        if (!productForm) return;
        if (productForm.querySelector('[ref="addToCartButton"]')) return;
        const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
        if (!form) return;
        form.requestSubmit();
      },
      true
    );
  }

  // The official sticky-add-to-cart component renders the button disabled when
  // the selected variant is sold out (and may re-disable it after variant
  // morphs). RIJ keeps it clickable so "SEE RELATED ITEMS" can scroll to the
  // recommendations — keep the disabled attribute off while sold out.
  if (!NS._stickyKeepEnabledWired) {
    NS._stickyKeepEnabledWired = true;
    const keepEnabled = () => {
      const bar = document.querySelector('sticky-add-to-cart');
      if (!bar || bar.dataset.variantAvailable !== 'false') return;
      const btn = bar.querySelector('[ref="addToCartButton"]');
      if (btn && btn.hasAttribute('disabled')) btn.removeAttribute('disabled');
    };
    keepEnabled();
    const mo = new MutationObserver(keepEnabled);
    const observeBar = () => {
      const bar = document.querySelector('sticky-add-to-cart');
      if (!bar) return;
      mo.observe(bar, { attributes: true, subtree: true, attributeFilter: ['disabled', 'data-variant-available'] });
    };
    observeBar();
    customElements.whenDefined('sticky-add-to-cart').then(observeBar).catch(() => {});
  }

  // -------------------------------------------------------------------------
  // Sold-out MAIN button: "SEE RELATED ITEMS" scrolls to the recommendations.
  // The button stays enabled on purpose (see applyBuyState) — intercept the
  // click in the capture phase so the (impossible) add-to-cart is never
  // submitted, and reuse the same recommendations target as the sticky bar
  // handler above. The sticky bar itself stays hidden for sold-out variants
  // (official behaviour) — this handler's guards skip clicks inside it.
  // -------------------------------------------------------------------------
  // The sold-out state is re-rendered by the theme's section fetch (morph), so
  // re-assert "enabled" whenever the buy buttons are patched while the variant
  // is unavailable — same idea as the sticky bar's keepEnabled observer.
  if (!NS._mainKeepEnabledWired) {
    NS._mainKeepEnabledWired = true;
    const keepMainEnabled = () => {
      const bar = document.querySelector('sticky-add-to-cart');
      const soldOut = !!bar && bar.dataset.variantAvailable === 'false';
      document.documentElement.classList.toggle('rij-sold-out', soldOut);
      if (!soldOut) return;
      document.querySelectorAll('button[name="add"]').forEach((b) => {
        if (b.closest('sticky-add-to-cart, product-card, quick-add-component')) return;
        if (b.disabled) b.disabled = false;
      });
    };
    keepMainEnabled();
    // Observe only the buy area. A body-wide subtree observer fires on every
    // unrelated disabled toggle (slideshow arrows / thumbnails) while swiping.
    // A morph can replace the container, so re-attach after those events.
    const mo = new MutationObserver(keepMainEnabled);
    const attach = () => {
      mo.disconnect();
      const box = document.querySelector('[ref="productFormButtons"]') || document.querySelector('product-form-component');
      if (box) mo.observe(box, { attributes: true, subtree: true, attributeFilter: ['disabled'] });
    };
    attach();
    document.addEventListener('variant:selected', () => setTimeout(() => { keepMainEnabled(); attach(); }, 0));
    document.addEventListener('shopify:section:load', () => setTimeout(() => { keepMainEnabled(); attach(); }, 0));
    customElements.whenDefined('sticky-add-to-cart').then(() => setTimeout(() => { keepMainEnabled(); attach(); }, 0)).catch(() => {});
  }

  if (!NS._mainSoldOutWired) {
    NS._mainSoldOutWired = true;
    document.addEventListener(
      'click',
      (e) => {
        let btn = e.target.closest?.('button[name="add"]');
        if (!btn) {
          // Insurance: a disabled button can swallow the press entirely, so if
          // the click landed anywhere in the sold-out buy area, use its button.
          const area = e.target.closest?.('[ref="productFormButtons"], .product-form-buttons');
          if (area) btn = area.querySelector('button[name="add"]');
        }
        if (!btn || btn.closest('product-card, quick-add-component')) return;
        if (btn.closest('sticky-add-to-cart')) return; // sticky has its own handler
        const bar = document.querySelector('sticky-add-to-cart');
        if (!bar || bar.dataset.variantAvailable !== 'false') return;
        const related = document.querySelector(
          'product-recommendations, .product-recommendations, [data-testid="product-recommendations-section"]'
        );
        if (!related) return;
        e.preventDefault();
        e.stopPropagation();
        related.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      true
    );
  }

})();